import { type Attributes, type FC, Suspense, type SuspenseProps } from 'react'
/**
 * HOC for creating a suspense boundary around any component, thereby making the component easier to mount in a scenario
 * where it will auto mange its own suspense state.
 *
 * @example
 * const SuspendedComponent = withSuspense(SomeComponent, {fallback: <Spinner/>})
 * return <SuspendedComponent {...someComponentProps} />
 */
export const withSuspense = <T,>(
  WrappedComponent: FC<T>,
  suspenseProps?: Omit<SuspenseProps, 'children'>,
) => {
  const SuspendedComponent = (props: T & Attributes) => (
    <Suspense {...suspenseProps}>
      <WrappedComponent {...props} />
    </Suspense>
  )

  SuspendedComponent.displayName = `${
    WrappedComponent.displayName || WrappedComponent.name || 'Unknown'
  }Suspended`

  return SuspendedComponent
}
